import { useMemo } from 'react';
import { useNavigate } from 'react-router';

export interface UseAppNavigationHook {
  navigateBack: () => void;
  navigateHome: (replace?: boolean) => void;
  navigateHomeSettings: () => void;
  navigateRoom: (roomId: string) => void;
  navigateLight: (roomId: string, lightId: string) => void;
  navigateLightingGroup: (roomId: string, groupId: string) => void;
  navigateSensors: (roomId: string, sensorsId: string) => void;
  navigateShades: (roomId: string, parentShadesId: string) => void;
  navigateTemperature: (roomId: string, temperatureThingId: string) => void;
  navigateTemperatureSchedule: (roomId: string, temperatureThingId: string) => void;
  navigateTemperatureAwayMode: (roomId: string, temperatureThingId: string) => void;
  navigateRegistration: () => void;
  navigateLogin: () => void;
  navigateAccount: () => void;
  navigateAbout: () => void;
  navigateEula: () => void;
  navigateVerifyAccount: () => void;
  navigateForgotPassword: () => void;
  navigateSetPassword: (email: string) => void;
  navigateAddPreset: (roomId: string, groupId: string) => void;
  navigateEditPreset: (roomId: string, groupId: string, presetId: string) => void;
  navigateEnergy: () => void;
  navigateCreateTicket: (id: number) => void;
  navigateEmailChange: () => void;
  navigateVerifyEmailChange: () => void;
  navigateDataConsent: () => void;
  isHome: () => boolean;
  isLoginPage: () => boolean;
}

export const useAppNavigation = (): UseAppNavigationHook => {
  const navigate = useNavigate();

  return useMemo(
    () => ({
      navigateBack: () => navigate(-1),
      navigateHome: (replace = false) => navigate('/home', { replace }),
      navigateHomeSettings: () => navigate('/home-settings'),
      navigateRoom: (roomId: string) => navigate(`/room/${roomId}`),
      navigateLight: (roomId: string, lightId: string) =>
        navigate(`/room/${roomId}/lighting/${lightId}`),
      navigateLightingGroup: (roomId: string, groupId: string) =>
        navigate(`/room/${roomId}/lights-group/${groupId}`),
      navigateSensors: (roomId: string, sensorsId: string) =>
        navigate(`/room/${roomId}/sensors/${sensorsId}`),
      navigateShades: (roomId: string, parentShadesId: string) =>
        navigate(`/room/${roomId}/shades/${parentShadesId}`),
      navigateTemperature: (roomId: string, temperatureThingId: string) =>
        navigate(`/room/${roomId}/temperature/${temperatureThingId}/thermostat`),
      navigateTemperatureSchedule: (roomId: string, temperatureThingId: string) =>
        navigate(`/room/${roomId}/temperature/${temperatureThingId}/schedule`),
      navigateTemperatureAwayMode: (roomId: string, temperatureThingId: string) =>
        navigate(`/room/${roomId}/temperature/${temperatureThingId}/away`),
      navigateRegistration: () => navigate('/register'),
      navigateLogin: () => navigate('/login'),
      navigateAccount: () => navigate('/account'),
      navigateAbout: () => navigate('/account/about'),
      navigateEula: () => navigate('/account/eula'),
      navigateVerifyAccount: () => navigate('/verify-account'),
      navigateForgotPassword: () => navigate('/forgot-password'),
      navigateSetPassword: (email: string) => navigate(`/reset-password/${email}`),
      navigateAddPreset: (roomId: string, groupId: string) =>
        navigate(`/room/${roomId}/lights-group/${groupId}/preset/new`),
      navigateEditPreset: (roomId: string, groupId: string, presetId: string) =>
        navigate(`/room/${roomId}/lights-group/${groupId}/preset/${presetId}`),
      navigateEnergy: () => navigate('/energy'),
      navigateCreateTicket: (id: number) => navigate(`/account/support/createTicket/${id}`),
      navigateEmailChange: () => navigate('/account/email'),
      navigateVerifyEmailChange: () => navigate('/account/email/verify'),
      navigateDataConsent: () => navigate('/account/consent'),
      isHome: () => location.pathname === '/home',
      isLoginPage: () => location.pathname === '/login',
    }),
    [navigate],
  );
};
